import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import ogHeroImage from "../../assets/padel-court.webp";
import { Link, createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero } from "../../components/site/Page";
import padel from "../../assets/padel-court.webp";
import { activityFeeText } from "@/lib/activity-fees";

const title = "Padel & tennis in the school week | SIS Estepona";
const description =
  "Padel and tennis for the school's distance-learning pupils in Estepona/Marbella: training Tuesday to Thursday 09:00–10:00 on courts next to the school.";

export const Route = createFileRoute("/en/padel-tennis")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      ...ogImage("/og/padel-v1.jpg"),
      { property: "og:url", content: absoluteUrl("/en/padel-tennis") },
      { property: "og:locale", content: "en_GB" },
    ],
    links: seoLinks("/en/padel-tennis"),
  }),
  component: PadelTennis,
});

const factsFor = (padelFee: string | null) => [
  { label: "Who can take part", value: "Distance-learning pupils only" },
  { label: "Location", value: "Courts right next to the school" },
  { label: "Training times", value: "Tuesday, Wednesday and Thursday 09:00–10:00" },
  { label: "Group size", value: "Maximum 4 pupils per group (school year 2026/27)" },
  { label: "Coach", value: "A dedicated coach for each group (school year 2026/27)" },
  { label: "Facilities", value: "Changing rooms and showers on site" },
  { label: "Price", value: padelFee ? `${padelFee}, in addition to the school fee` : "See the fees page for the current price" },
];

const training = [
  {
    heading: "Technique",
    text: "Grip, strokes and serve are built up step by step, repeated every week so it sticks.",
  },
  {
    heading: "Game understanding",
    text: "Court positioning, working with your partner in padel and reading the opponent's game.",
  },
  {
    heading: "Movement and fitness",
    text: "Footwork, balance and warm-ups that reduce the risk of injury and make the pupil quicker on court.",
  },
  {
    heading: "Match play",
    text: "Points and matches where pupils apply what they have practised, with direct feedback from the coach.",
  },
];

function PadelTennis() {
  const padelFee = activityFeeText("padel", "en");
  const facts = factsFor(padelFee);
  return (
    <>
      <PageHero
        eyebrow="Sports profile"
        title="Padel & tennis on the Costa del Sol"
        intro="A sports programme for the school's distance-learning pupils, built into the school week. Pupils train on courts next to the school three mornings a week, in groups of no more than four, and are back at their school work straight afterwards."
      />

      <section className="mx-auto max-w-5xl px-5 py-14 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start">
          <div className="min-w-0 space-y-10">
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">How it works</h2>
              <p className="text-base leading-relaxed text-foreground/90">
                Training takes place on Tuesday, Wednesday and Thursday from 09:00 to 10:00 on courts
                right next to the school. Because the courts are on site, nobody needs to drive
                anywhere and no afternoon is lost to travel. Changing rooms and showers are next to
                the courts, so pupils return to the lesson changed and ready.
              </p>
              <p className="text-base leading-relaxed text-foreground/90">
                 In the 2026/27 school year, every group has its own coach and a maximum of four pupils per court. That size is deliberate:
                everyone hits plenty of balls, everyone is seen, and the coach has time to correct
                details during the session rather than simply running a drill. The programme is offered only
                to our distance-learning pupils in secondary school and upper secondary – the
                timetable is arranged so that school work is not affected.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">Who is it for?</h2>
              <p className="text-base leading-relaxed text-foreground/90">
                The programme is for distance-learning pupils only. It is open both to pupils who
                already play regularly and want to keep developing, and to complete beginners who
                want to learn from scratch. Groups are set
                by age and level, so nobody has to keep up with a pace that is too fast or wait
                around in one that is too slow. If your child has never held a racket, that is fine –
                equipment can be borrowed to begin with.
              </p>
            </div>

            <div className="space-y-5">
              <h2 className="text-2xl font-semibold">What pupils work on</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {training.map((t) => (
                  <div
                    key={t.heading}
                    className="rounded-xl border border-border bg-card p-5 shadow-soft"
                  >
                    <h3 className="text-base font-semibold">{t.heading}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">Cost and how to sign up</h2>
              <p className="text-base leading-relaxed text-foreground/90">
                The padel and tennis programme costs{" "}
                <strong>{padelFee ?? "the amount listed on the fees page"}</strong>, payable in
                addition to the ordinary school fee. The price covers the three weekly sessions for
                the whole term, coaching and access to courts and changing rooms.
              </p>
              <p className="text-base leading-relaxed text-foreground/90">
                You register your interest directly in the application form – tick the padel and
                tennis programme for the child concerned and we will come back to you with group
                allocation and start time ahead of the term. If you are unsure whether the programme
                suits your child, get in touch first and we will go through level and set-up
                together.
              </p>
              <div className="flex flex-col gap-3 pt-1 sm:flex-row">
                <Link
                  to="/en/application"
                  className="flex min-h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
                >
                  Apply for padel &amp; tennis
                </Link>
                <Link
                  to="/en/contact-us"
                  className="flex min-h-11 items-center justify-center rounded-md border border-border px-5 text-sm font-medium transition-colors hover:bg-sand"
                >
                  Ask a question
                </Link>
              </div>
            </div>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24">
            <img
              src={padel}
              alt="Padel court with glass walls next to the school on the Costa del Sol"
              width={1600}
              height={1008}
              loading="lazy"
              className="aspect-[4/3] w-full rounded-xl object-cover shadow-soft"
            />
            <dl className="rounded-xl border border-border bg-card p-6 shadow-soft">
              {facts.map((f) => (
                <div key={f.label} className="border-b border-border/60 py-3 first:pt-0 last:border-0 last:pb-0">
                  <dt className="text-[0.7rem] font-semibold tracking-[0.18em] text-primary uppercase">
                    {f.label}
                  </dt>
                  <dd className="mt-1 text-sm leading-relaxed">{f.value}</dd>
                </div>
              ))}
            </dl>
          </aside>
        </div>
      </section>

      <CtaBand
        title="Want to know more about the sports programme?"
        text="Get in touch and we'll explain how training fits into the timetable and which group suits your child."
      />
      <div className="h-20" />
    </>
  );
}
